import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, RefreshCw } from 'lucide-react';
import { toast } from 'react-toastify';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useConfirm } from '@/contexts/confirmContext';
import { platformAdminApi } from '../api/platformAdminApi';
import type { PlatformTenant } from '../types/platformAdmin.types';

export function TenantsPage() {
  const navigate = useNavigate();
  const { confirm } = useConfirm();
  const [tenants, setTenants] = useState<PlatformTenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const filteredTenants = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return tenants;
    return tenants.filter(
      tenant =>
        tenant.name.toLowerCase().includes(query) ||
        (tenant.slug || '').toLowerCase().includes(query)
    );
  }, [tenants, search]);

  const loadTenants = async () => {
    try {
      setLoading(true);
      setTenants(await platformAdminApi.listTenants());
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Error al cargar tenants');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTenants();
  }, []);

  const handleDeactivate = async (tenant: PlatformTenant) => {
    const accepted = await confirm({
      title: 'Desactivar tenant',
      message: `¿Desactivar ${tenant.name}? Sus admins no podrán operar mientras esté inactivo.`,
      confirmText: 'Desactivar',
      cancelText: 'Cancelar',
      variant: 'danger',
    });
    if (!accepted) return;
    try {
      await platformAdminApi.deleteTenant(tenant.id);
      toast.success('Tenant desactivado');
      await loadTenants();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Error al desactivar tenant');
    }
  };

  return (
    <div className='container mx-auto py-6 space-y-6'>
      <Card>
        <CardHeader className='flex flex-row items-center justify-between'>
          <div>
            <CardTitle>Tenants</CardTitle>
            <CardDescription>Administra clientes, límites y features habilitadas.</CardDescription>
          </div>
          <div className='flex gap-2'>
            <Button variant='outline' onClick={loadTenants} disabled={loading}>
              <RefreshCw className='h-4 w-4 mr-2' />
              Actualizar
            </Button>
            <Button onClick={() => navigate('/admin/tenants/new')}>
              <Plus className='h-4 w-4 mr-2' />
              Nuevo Tenant
            </Button>
          </div>
        </CardHeader>
        <CardContent className='space-y-4'>
          <Input
            value={search}
            onChange={event => setSearch(event.target.value)}
            placeholder='Buscar por nombre o slug...'
            className='max-w-md'
          />

          <div className='border rounded-lg overflow-hidden'>
            <table className='w-full text-sm'>
              <thead className='bg-muted/50'>
                <tr>
                  <th className='text-left p-3'>Nombre</th>
                  <th className='text-left p-3'>Estado</th>
                  <th className='text-left p-3'>Límites</th>
                  <th className='text-left p-3'>Features</th>
                  <th className='text-right p-3'>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredTenants.map(tenant => (
                  <tr key={tenant.id} className='border-t'>
                    <td className='p-3'>
                      <div className='font-medium'>{tenant.name}</div>
                      <div className='text-xs text-muted-foreground'>{tenant.slug}</div>
                    </td>
                    <td className='p-3'>{tenant.status === 'ACTIVE' ? 'Activo' : 'Inactivo'}</td>
                    <td className='p-3'>
                      {tenant.usersCount ?? 0}/{tenant.config.maxUsers} usuarios ·{' '}
                      {tenant.botsCount ?? 0}/{tenant.config.maxBots} bots
                    </td>
                    <td className='p-3'>{tenant.config.features.join(', ')}</td>
                    <td className='p-3'>
                      <div className='flex justify-end gap-2'>
                        <Button
                          variant='outline'
                          size='sm'
                          onClick={() => navigate(`/admin/tenants/${tenant.id}`)}
                        >
                          Editar
                        </Button>
                        <Button
                          variant='destructive'
                          size='sm'
                          onClick={() => handleDeactivate(tenant)}
                        >
                          Desactivar
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!loading && filteredTenants.length === 0 && (
                  <tr>
                    <td colSpan={5} className='p-6 text-center text-muted-foreground'>
                      No hay tenants para mostrar.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
